import { useEffect, useRef, useState } from 'react';
import type { CampaignSummary } from '@monitoring-panel/types';
import {
  BarChart3,
  ChevronDown,
  Database,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  type LucideIcon,
} from 'lucide-react';
import { ReportLinkButton } from './ReportLinkButton';

interface CardLink {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Kept on the card in collapsed mode; the rest go to the "Más" menu. */
  primary: boolean;
}

/** The platform links an execution has, in the order the card shows them. */
function cardLinks(campaign: CampaignSummary): CardLink[] {
  const links: Array<CardLink | null> = [
    campaign.opportunityId
      ? { label: 'Opportunity', href: `https://os.advia.tech/opportunities/${campaign.opportunityId}`, icon: FileText, primary: false }
      : null,
    { label: 'Advia OS', href: `https://os.advia.tech/delivery?exec=${campaign.executionRecordId}`, icon: FileText, primary: true },
    campaign.clientReport ? { label: 'Client Report', href: campaign.clientReport, icon: FileSpreadsheet, primary: false } : null,
    campaign.notionPageId
      ? { label: 'Notion', href: `https://www.notion.so/${campaign.notionPageId}`, icon: FileText, primary: false }
      : null,
    campaign.platformUrl ? { label: 'DV360', href: campaign.platformUrl, icon: BarChart3, primary: true } : null,
    campaign.adserverUrl ? { label: 'Adserver', href: campaign.adserverUrl, icon: Database, primary: false } : null,
    campaign.auxOpportunityFolder ? { label: 'Drive', href: campaign.auxOpportunityFolder, icon: FolderOpen, primary: false } : null,
  ];
  return links.filter((link): link is CardLink => link !== null);
}

interface CardLinksProps {
  campaign: CampaignSummary;
  /** Only fetch the report link when the card is on screen. */
  enabled: boolean;
  /** One row whatever the width: the main links as buttons, the rest under "Más". */
  collapsed: boolean;
}

export function CardLinks({ campaign, enabled, collapsed }: CardLinksProps) {
  const links = cardLinks(campaign);
  const shown = collapsed ? links.filter((link) => link.primary) : links;
  const more = collapsed ? links.filter((link) => !link.primary) : [];

  return (
    <div className={collapsed ? 'flex flex-nowrap items-center gap-2' : 'flex flex-wrap gap-2'}>
      <ReportLinkButton executionId={campaign.executionId} enabled={enabled} shortLabel={collapsed} />
      {shown.map((link) => (
        <a
          key={link.label}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="adv-btn adv-btn--secondary adv-btn--sm min-h-[44px] sm:min-h-0 whitespace-nowrap"
        >
          <link.icon size={14} className="flex-shrink-0" />
          {link.label}
          <ExternalLink size={12} className="opacity-60 flex-shrink-0" />
        </a>
      ))}
      {more.length > 0 && <MoreLinksMenu links={more} />}
    </div>
  );
}

function MoreLinksMenu({ links }: { links: CardLink[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className="adv-btn adv-btn--secondary adv-btn--sm min-h-[44px] sm:min-h-0 whitespace-nowrap"
      >
        Más
        <ChevronDown size={14} className="flex-shrink-0" />
      </button>
      {isOpen && (
        <div className="adv-dropdown absolute left-0 bottom-full mb-2 w-48 z-20">
          {links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="adv-dropdown__item"
            >
              <link.icon size={14} className="flex-shrink-0" />
              <span className="flex-1">{link.label}</span>
              <ExternalLink size={12} className="opacity-60 flex-shrink-0" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
